import * as React from 'react'

import { computed, makeObservable, observable } from 'mobx'
import { observer } from 'mobx-react'

import {
  Radio,
  RadioGroup,
} from '~/client/src/shared/components/shadcn-ui/radio-group'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import LocationAttributeBase from '~/client/src/shared/models/LocationObjects/LocationAttributeBase'

import SitemapAttributeTag from '../../../SitemapAttributeTag/SitemapAttributeTag'
import DeliveryDetailsStore from '../../DeliveryDetails.store'
import DeliveryConfirm from './DeliveryConfirm'

// localization: translated

interface IProps {
  title: string
  isShown: boolean
  onApply: () => void
  store: DeliveryDetailsStore
}

@observer
export default class DeliveryRecurringEditConfirm extends React.Component<IProps> {
  @observable private store: DeliveryDetailsStore = null
  public constructor(props: IProps) {
    super(props)
    makeObservable(this)

    this.store = props.store
  }

  public render() {
    const { isShown, title } = this.props
    const {
      change_verb,
      ok,
      deliveryDescriptions,
      thisDelivery,
      thisAndFutureDeliveries,
      allDeliveries,
    } = Localization.translator

    return (
      <DeliveryConfirm
        title={title || change_verb}
        isShown={isShown}
        onHide={this.onHide}
        onApply={this.onApply}
        applyButtonText={ok}
        isApplyDisabled={this.isApplyBtnDisabled}
      >
        <RadioGroup
          className="tw:flex tw:flex-col tw:gap-2"
          value={this.selectedRecurringValue}
          onValueChange={this.onRecurringValueChange}
        >
          {this.isThisDeliveryOptionShown && (
            <label className="tw:inline-flex tw:items-center tw:gap-2 tw:cursor-pointer tw:select-none">
              <Radio value="this" />
              <span>{thisDelivery}</span>
            </label>
          )}
          <label className="tw:inline-flex tw:items-center tw:gap-2 tw:cursor-pointer tw:select-none">
            <Radio value="thisAndFuture" />
            <span>{thisAndFutureDeliveries}</span>
          </label>
          {this.areAllOptionsShown && (
            <label className="tw:inline-flex tw:items-center tw:gap-2 tw:cursor-pointer tw:select-none">
              <Radio value="all" />
              <span>{allDeliveries}</span>
            </label>
          )}
        </RadioGroup>
        {!!this.overlappedAttrByRecurringDeliveries && (
          <div className="col x-center no-grow">
            <span className="text bold large red py5">
              {deliveryDescriptions.maximumDeliveriesAreScheduled}:
            </span>
            <SitemapAttributeTag
              shouldShowAsTag={true}
              dataObject={this.overlappedAttrByRecurringDeliveries}
            >
              <span
                title={this.overlappedAttrByRecurringDeliveries.name}
                className="text large"
              >
                {this.overlappedAttrByRecurringDeliveries.name}
              </span>
            </SitemapAttributeTag>
          </div>
        )}
      </DeliveryConfirm>
    )
  }

  @computed
  private get selectedRecurringValue(): string {
    if (this.areAllOptionsShown && this.areAllDeliveriesSelected) return 'all'
    if (this.isThisAndFutureSelected) return 'thisAndFuture'
    if (
      this.isThisDeliveryOptionShown &&
      !this.store.recurringRelatedIds?.length
    ) {
      return 'this'
    }
    return ''
  }

  private onRecurringValueChange = (value: string) => {
    const { clearRecurringRelatedIds, setRecurringRelatedIds } = this.store
    switch (value) {
      case 'this':
        clearRecurringRelatedIds()
        return
      case 'thisAndFuture':
        setRecurringRelatedIds(false)
        return
      case 'all':
        setRecurringRelatedIds(true)
        return
    }
  }

  private onHide = () => {
    const { hideEditRecurringDialog, clearRecurringRelatedIds } =
      this.props.store

    hideEditRecurringDialog()
    clearRecurringRelatedIds()
  }

  private onApply = () => {
    if (!this.isAnyOptionChecked || this.isApplyBtnDisabled) {
      return
    }

    const {
      onApply,
      store: { hideEditRecurringDialog },
    } = this.props

    onApply()
    hideEditRecurringDialog()
  }

  @computed
  private get isThisDeliveryOptionShown(): boolean {
    return !this.store.isRecurringOptionsChanged
  }

  @computed
  private get isThisAndFutureSelected(): boolean {
    return this.store.areFollowingRecurringsSelected
  }

  @computed
  private get areAllOptionsShown(): boolean {
    const { followingRecurringDeliveries, allRecurringDeliveries } = this.store

    return allRecurringDeliveries.length !== followingRecurringDeliveries.length
  }

  @computed
  private get areAllDeliveriesSelected(): boolean {
    const { recurringRelatedIds, allRecurringDeliveries } = this.store

    return (
      !!allRecurringDeliveries?.length &&
      recurringRelatedIds?.length === allRecurringDeliveries.length
    )
  }

  @computed
  private get isAnyOptionChecked(): boolean {
    return (
      (this.isThisDeliveryOptionShown &&
        !this.store.recurringRelatedIds?.length) ||
      this.isThisAndFutureSelected ||
      (this.areAllOptionsShown && this.areAllDeliveriesSelected)
    )
  }

  @computed
  private get overlappedAttrByRecurringDeliveries(): LocationAttributeBase {
    if (!this.isThisAndFutureSelected && !this.areAllDeliveriesSelected) {
      return null
    }

    return this.store.overlappedAttrByRecurringDeliveries
  }

  @computed
  private get isApplyBtnDisabled(): boolean {
    return (
      !this.isAnyOptionChecked || !!this.overlappedAttrByRecurringDeliveries
    )
  }
}
